import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useForm, FormProvider, useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { CloseButton, Divider, FullRow, GridItem, Row, TextButton, TextField, Typography, WithInfoIcon, useStateInRef } from 'seagull';
import { CloseCross } from 'seagull/icons';
import { draftLogicTextsPath, reviewerEmailValidator, reviewerNameValidator } from '~/business-logic/draftLogic';
import SignatoryCheckbox from '~/components/AgreementStudioPage/AgreementStudioProposalMode/SignatoryCheckbox/SignatoryCheckbox';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './AddAdditionalRecipients.module.scss';
function AdditionalRecipientRow(props) {
    const { idx, emailRules, disabled, trigger, onRemove, inBottomSheet } = props;
    const { t: additionalRecipientsTexts } = useLocaleKeys('components.agreementStudio.additionalRecipients');
    const { control } = useFormContext();
    const firstNameField = /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "additionalRecipients.".concat(idx, ".firstName"),
        rules: reviewerNameValidator,
        render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                collapseHelperText: false,
                mobile: {
                    renderAsCard: true
                },
                testId: "reviewer-".concat(idx, "-first-name"),
                bi: {
                    name: 'recipient_first_name'
                },
                label: additionalRecipientsTexts.firstName(),
                disabled: disabled
            }, connectControllerToTextField(p)))
    });
    const lastNameField = /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "additionalRecipients.".concat(idx, ".lastName"),
        rules: reviewerNameValidator,
        render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                collapseHelperText: false,
                mobile: {
                    renderAsCard: true
                },
                testId: "reviewer-".concat(idx, "-last-name"),
                bi: {
                    name: 'recipient_last_name'
                },
                label: additionalRecipientsTexts.lastName(),
                disabled: disabled
            }, connectControllerToTextField(p)))
    });
    const emailField = /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "additionalRecipients.".concat(idx, ".email"),
        rules: emailRules,
        render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                collapseHelperText: false,
                mobile: {
                    renderAsCard: true
                },
                testId: "reviewer-".concat(idx, "-email"),
                bi: {
                    name: 'recipient_email'
                },
                label: additionalRecipientsTexts.email(),
                disabled: disabled
            }, connectControllerToTextField(p)), {
                onChange: (e)=>{
                    p.field.onChange(e);
                    trigger('additionalRecipients');
                }
            }))
    });
    const signatoryCheckbox = /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "additionalRecipients.".concat(idx, ".isSignatory"),
        render: (p)=>/*#__PURE__*/ _jsx(SignatoryCheckbox, {
                disableInfoIcon: inBottomSheet,
                infoTestId: "reviewer-".concat(idx, "-required-sign-info"),
                info: additionalRecipientsTexts.infoSignatories(),
                disabled: disabled,
                checkboxTestId: "reviewer-".concat(idx, "-required-sign"),
                onChange: p.field.onChange,
                value: p.field.value
            })
    });
    const row = inBottomSheet ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                gap: 8,
                children: [
                    firstNameField,
                    lastNameField
                ]
            }),
            /*#__PURE__*/ _jsx(FullRow, {
                children: emailField
            }),
            /*#__PURE__*/ _jsxs(FullRow, {
                alignItems: "center",
                justifyContent: "space-between",
                children: [
                    signatoryCheckbox,
                    !disabled && /*#__PURE__*/ _jsx(TextButton, {
                        underline: "none",
                        testId: "reviewer-".concat(idx, "-remove"),
                        bi: {
                            name: 'remove_recipient'
                        },
                        prefixIcon: /*#__PURE__*/ _jsx(CloseCross, {
                            size: 12
                        }),
                        onClick: onRemove,
                        children: additionalRecipientsTexts.remove()
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Divider, {
                margin: 0
            })
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(FullRow, {
                alignItems: "center",
                gap: 8,
                children: [
                    firstNameField,
                    lastNameField,
                    emailField,
                    !disabled && /*#__PURE__*/ _jsx(CloseButton, {
                        skin: "transparent",
                        testId: "reviewer-".concat(idx, "-remove"),
                        bi: {
                            name: 'remove_recipient'
                        },
                        onClick: onRemove
                    })
                ]
            }),
            signatoryCheckbox
        ]
    });
    return row;
}
export default function AddAdditionalRecipients(props) {
    const { initialState, showReviewerErrors, primaryContact, amendmentInfoCallout, disabled, inBottomSheet } = props;
    const { t: additionalRecipientsTexts } = useLocaleKeys('components.agreementStudio.additionalRecipients');
    const { t: validationTexts } = useLocaleKeys(draftLogicTextsPath);
    const getProps = useStateInRef(props);
    const wasChangedRef = useRef(false);
    const isPrimaryASampleContact = primaryContact === null || primaryContact === void 0 ? void 0 : primaryContact.isSample();
    const form = useForm({
        mode: 'all',
        defaultValues: {
            additionalRecipients: initialState.map((r)=>({
                    firstName: r.firstName || '',
                    lastName: r.lastName || '',
                    email: r.email || '',
                    isSignatory: r.isSignatory || false
                }))
        }
    });
    const recipientsControl = useFieldArray({
        control: form.control,
        name: 'additionalRecipients'
    });
    const recipients = form.watch('additionalRecipients');
    const hasRecipients = recipientsControl.fields.length > 0;
    // react-hook-form doesn't work immutably for an array when changing inputs, so we need to use the stringified version to compare changes.
    const stringifiedRecipients = JSON.stringify(form.watch('additionalRecipients'));
    const emailRules = useMemo(()=>{
        const primaryAndRecipients = (primaryContact ? [
            {
                email: primaryContact.email()
            }
        ] : []).concat(recipients);
        return reviewerEmailValidator(primaryAndRecipients, validationTexts);
    }, [
        primaryContact,
        recipients
    ]);
    useEffect(()=>{
        // We need to check for duplicate emails after removing a row, or changing primaryContact.
        form.trigger('additionalRecipients');
    }, [
        primaryContact,
        recipients.length
    ]);
    useEffect(()=>{
        if (form.formState.isDirty || wasChangedRef.current) {
            // react-hook-form doesn't work immutably for an array when changing inputs, so we need to clone the array.
            const newValue = [
                ...form.getValues().additionalRecipients
            ];
            getProps().onChange(newValue);
            wasChangedRef.current = true;
        }
    }, [
        stringifiedRecipients
    ]);
    useEffect(()=>{
        if (showReviewerErrors) {
            // Will auto-focus on the first invalid field.
            form.handleSubmit(()=>{})();
        }
    }, [
        showReviewerErrors
    ]);
    useEffect(()=>{
        if (isPrimaryASampleContact) {
            const emptyReviewers = [];
            form.setValue('additionalRecipients', emptyReviewers);
            getProps().onChange(emptyReviewers);
        }
    }, [
        isPrimaryASampleContact
    ]);
    const AddReviewer = /*#__PURE__*/ _jsx(TextButton, {
        underline: "none",
        testId: "add-reviewer",
        bi: {
            name: 'add_additional_recipient'
        },
        disabled: disabled,
        onClick: ()=>recipientsControl.append({
                firstName: '',
                lastName: '',
                email: '',
                isSignatory: false
            }),
        children: hasRecipients ? additionalRecipientsTexts.addAnAdditionalRecipient() : additionalRecipientsTexts.addAdditionalRecipients()
    });
    const AdditionalRecipientsWithInfoIon = (param)=>{
        let { children } = param;
        return /*#__PURE__*/ _jsx(WithInfoIcon, {
            showInfo: !inBottomSheet,
            info: additionalRecipientsTexts.infoDescription(),
            children: children
        });
    };
    return /*#__PURE__*/ _jsxs(FormProvider, _object_spread_props(_object_spread({}, form), {
        children: [
            /*#__PURE__*/ _jsxs(GridItem, {
                className: cn(styles.root, {
                    [styles.adjustUpwards]: !hasRecipients && !amendmentInfoCallout && !inBottomSheet,
                    [styles.inBottomSheet]: inBottomSheet
                }),
                span: 12,
                children: [
                    hasRecipients && /*#__PURE__*/ _jsx(AdditionalRecipientsWithInfoIon, {
                        children: /*#__PURE__*/ _jsx(Typography, {
                            weight: "normal",
                            children: additionalRecipientsTexts.additionalRecipientsTitle()
                        })
                    }),
                    recipientsControl.fields.map((r, idx)=>/*#__PURE__*/ _jsx(AdditionalRecipientRow, {
                            inBottomSheet: inBottomSheet,
                            idx: idx,
                            setFocus: form.setFocus,
                            trigger: form.trigger,
                            emailRules: emailRules,
                            disabled: disabled,
                            onRemove: ()=>recipientsControl.remove(idx)
                        }, r.id)),
                    !isPrimaryASampleContact && recipientsControl.fields.length < 10 && (hasRecipients ? /*#__PURE__*/ _jsx(_Fragment, {
                        children: AddReviewer
                    }) : /*#__PURE__*/ _jsx(AdditionalRecipientsWithInfoIon, {
                        children: AddReviewer
                    })),
                    amendmentInfoCallout
                ]
            }),
            hasRecipients && !inBottomSheet && /*#__PURE__*/ _jsx(GridItem, {
                className: styles.separator,
                span: 12
            })
        ]
    }));
}
